import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { blogPosts } from "@/data/blogPosts";
import { businessId, site, telephoneHref } from "@/lib/site";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const relatedPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  // JSON-LD structured data
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    url: `${site.siteUrl}/blog/${post.slug}`,
    author: {
      "@type": "Person",
      name: "Chris Potter",
      jobTitle: "Owner",
      worksFor: { "@id": businessId },
    },
    publisher: {
      "@type": "Organization",
      name: site.shortName,
      url: site.siteUrl,
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${site.siteUrl}/` },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${site.siteUrl}/blog` },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": `${site.siteUrl}/blog/${post.slug}` },
    ]
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

      <article>
        {/* Hero */}
        <header className="py-12 md:py-16" style={{ background: "var(--hero-gradient)" }}>
          <div className="container max-w-3xl">
            <Link to="/blog" className="inline-flex items-center gap-1 text-primary-foreground/70 hover:text-primary-foreground text-sm mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-primary-foreground mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-primary-foreground/70">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="py-12 md:py-16">
          <div className="container max-w-3xl">
            <div className="prose prose-lg max-w-none">
              {post.content.map((block, i) => {
                if (block.startsWith("## ")) {
                  return (
                    <h2 key={i} className="text-xl md:text-2xl font-heading font-bold mt-8 mb-4 text-foreground">
                      {block.replace("## ", "")}
                    </h2>
                  );
                }
                return (
                  <p key={i} className="text-muted-foreground leading-relaxed mb-4">
                    {block}
                  </p>
                );
              })}
            </div>

            {/* CTA inline */}
            <div className="mt-12 p-6 md:p-8 rounded-xl bg-muted border">
              <h3 className="font-heading font-bold text-lg mb-2">Ready to Get Started?</h3>
              <p className="text-muted-foreground text-sm mb-4">
                 Contact Professional Property Cleanout for a scope-specific estimate on interior demolition or related property work in Northeast Wisconsin.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild>
                   <Link to="/book">Request an Estimate</Link>
                </Button>
                <Button variant="outline" asChild>
                  <a href={telephoneHref} className="flex items-center gap-2">
                    <Phone className="w-4 h-4" /> {site.phone}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-muted">
          <div className="container max-w-3xl">
            <h2 className="text-xl font-heading font-bold mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {relatedPosts.map((rp) => (
                <Card key={rp.slug} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <h3 className="font-heading font-semibold text-sm mb-2 line-clamp-2">
                      <Link to={`/blog/${rp.slug}`} className="hover:text-primary transition-colors">
                        {rp.title}
                      </Link>
                    </h3>
                    <p className="text-muted-foreground text-xs line-clamp-2">{rp.excerpt}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default BlogPost;
